import { type Artifact, type ReviewStatus, type TableArtifactData, type TableRow, markArtifactResponded } from "@shared";

/** Row id → reviewer state. Lives in component `useState`, not the channel. */
export type StatusMap = Record<string, ReviewStatus>;

export type ReviewAction =
  | { type: "accept"; id: string }
  | { type: "decline"; id: string }
  | { type: "approveAll" }
  | { type: "rejectRest" }
  | { type: "clearAll" };

/** Seed local state from the rows' persisted statuses (pending if unset). */
export function initialStatuses(rows: TableRow[]): StatusMap {
  return Object.fromEntries(rows.map((r) => [r.id, r.status ?? "pending"]));
}

function mapValues(statuses: StatusMap, fn: (s: ReviewStatus) => ReviewStatus): StatusMap {
  const next: StatusMap = {};
  for (const [id, s] of Object.entries(statuses)) next[id] = fn(s);
  return next;
}

/**
 * Apply one review action. Per-row accept/decline toggle off when re-clicked
 * (back to pending). "Approve all" accepts everything; "Reject rest" declines
 * everything not already accepted; "Clear all" resets everything to pending
 * (the un-do of Approve all).
 */
export function reduceStatuses(statuses: StatusMap, action: ReviewAction): StatusMap {
  switch (action.type) {
    case "accept":
      return { ...statuses, [action.id]: statuses[action.id] === "accepted" ? "pending" : "accepted" };
    case "decline":
      return { ...statuses, [action.id]: statuses[action.id] === "declined" ? "pending" : "declined" };
    case "approveAll":
      return mapValues(statuses, () => "accepted");
    case "rejectRest":
      return mapValues(statuses, (s) => (s === "accepted" ? "accepted" : "declined"));
    case "clearAll":
      return mapValues(statuses, () => "pending");
  }
}

/**
 * Component-level review state: the per-row statuses, plus a memo of which rows
 * the last "Skip rest" turned pending→declined — so that click can be undone.
 */
export interface ReviewState {
  statuses: StatusMap;
  skippedByRest: string[];
}

export function initialReviewState(rows: TableRow[]): ReviewState {
  return { statuses: initialStatuses(rows), skippedByRest: [] };
}

/**
 * Top-level reducer. "Skip rest" is a toggle: the first click declines every
 * not-yet-accepted row and remembers which *pending* rows it changed; the next
 * click restores exactly those to pending (leaving rows you'd already declined
 * by hand untouched). Any other action commits the skip — clears the memo.
 */
export function reduceReview(state: ReviewState, action: ReviewAction): ReviewState {
  if (action.type === "rejectRest") {
    if (state.skippedByRest.length > 0) {
      const statuses = { ...state.statuses };
      for (const id of state.skippedByRest) {
        if (statuses[id] === "declined") statuses[id] = "pending";
      }
      return { statuses, skippedByRest: [] };
    }
    const skippedByRest = Object.entries(state.statuses)
      .filter(([, s]) => s === "pending")
      .map(([id]) => id);
    return { statuses: reduceStatuses(state.statuses, action), skippedByRest };
  }
  return { statuses: reduceStatuses(state.statuses, action), skippedByRest: [] };
}

export interface ReviewCounts {
  approved: number;
  rejected: number;
  open: number;
}

export function statusCounts(statuses: StatusMap): ReviewCounts {
  const counts: ReviewCounts = { approved: 0, rejected: 0, open: 0 };
  for (const s of Object.values(statuses)) {
    if (s === "accepted") counts.approved += 1;
    else if (s === "declined") counts.rejected += 1;
    else counts.open += 1;
  }
  return counts;
}

/**
 * The whole-artifact submit payload: the artifact with each row stamped with its
 * current local status, and the artifact itself marked responded (`completed`)
 * so it renders read-only afterward. This is what Approve hands to
 * `submit({ artifacts: [...] })` — the reducer whole-replaces by id, the
 * next-turn reminder diffs the statuses.
 */
export function applyStatuses(artifact: Artifact, statuses: StatusMap): Artifact {
  const data = artifact.data as TableArtifactData;
  return markArtifactResponded({
    ...artifact,
    data: {
      ...data,
      rows: data.rows.map((row) => ({ ...row, status: statuses[row.id] ?? row.status ?? "pending" })),
    },
  });
}
